/**
 * Phase 87av — Détail / édition d'un courrier corporate.
 * Route : /courriers/:id (admin only)
 *
 * Composants :
 * - Formulaire destinataire (norme suisse : nom, société, rue, NPA, ville, pays)
 * - Type libre (Input texte, choix utilisateur 2b)
 * - Date + objet + corps Tiptap (RichTextEditor)
 * - PDF généré via CompanyLetterPdf (logo + signature + footer société)
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Save, Trash2, Loader2, Download, Copy, Archive, ArchiveRestore, FileText,
} from "lucide-react";
import { pdf } from "@react-pdf/renderer";
import { CompanyLetterPdf } from "@/components/CompanyLetterPdf";
import { RichTextEditor } from "@/components/RichTextEditor";
import { showPdfPreview } from "@/components/PdfPreview";
import { useConfirm } from "@/components/ConfirmDialog";
import { Switch } from "@/components/ui/switch";
import { AddressBookPicker } from "@/components/AddressBookPicker";

// Suggestions de types fréquents (proposés en datalist, mais l'admin peut taper ce qu'il veut)
const TYPE_SUGGESTIONS = [
  "Information",
  "Avertissement",
  "Communication distributeur",
  "Communication commerciale",
  "Rappel",
  "Annonce",
  "Vœux",
  "Information clients",
  "Avertissement revendeur",
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-CH", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso; }
};

// Petite zone "Field" alignée — utilisée partout pour la cohérence visuelle.
function Field({ label, value, onChange, type = "text", placeholder, testid, list, wrapperClass = "" }) {
  return (
    <div className={wrapperClass}>
      <label className="block text-xs uppercase tracking-wider text-[#575A57] mb-1">{label}</label>
      <Input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        list={list}
        data-testid={testid}
      />
    </div>
  );
}

export default function CompanyLetterDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [letter, setLetter] = useState(null);
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [linkedContact, setLinkedContact] = useState(null);  // Phase 101df-v2

  const load = async () => {
    try {
      const [l, s] = await Promise.all([
        api.get(`/company-letters/${id}`),
        api.get("/settings?include_logo=true").catch(() => ({ data: {} })),
      ]);
      // Defensive : assure que body_json est un doc Tiptap valide
      const data = l.data || {};
      if (!data.body_json || !data.body_json.type) {
        data.body_json = { type: "doc", content: [{ type: "paragraph" }] };
      }
      setLetter(data);
      setSettings(s.data || {});
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement");
      navigate("/courriers");
    }
  };

  useEffect(() => { load(); }, [id]);

  const update = (patch) => {
    setLetter((l) => ({ ...l, ...patch }));
    setDirty(true);
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      await api.put(`/company-letters/${id}`, {
        letter_type: letter.letter_type || "Information",
        subject: letter.subject || "",
        body_json: letter.body_json || { type: "doc", content: [{ type: "paragraph" }] },
        body_plain: letter.body_plain || "",
        recipient_name: letter.recipient_name || "",
        recipient_company: letter.recipient_company || "",
        recipient_address: letter.recipient_address || "",
        recipient_postal_code: letter.recipient_postal_code || "",
        recipient_city: letter.recipient_city || "",
        recipient_country: letter.recipient_country || "Suisse",
        recipient_email: letter.recipient_email || "",
        letter_date: letter.letter_date || null,
        include_signature_image: letter.include_signature_image === true,
      });
      toast.success("Courrier enregistré");
      setDirty(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const removeLetter = async () => {
    const ok = await confirm({
      title: "Supprimer ce courrier ?",
      description: "Action irréversible.",
      confirmText: "Supprimer",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/company-letters/${id}`);
      toast.success("Courrier supprimé");
      navigate("/courriers");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const duplicate = async () => {
    try {
      if (dirty) {
        const ok = await confirm({
          title: "Modifications non enregistrées",
          description: "La duplication créera une copie de l'état sauvegardé. Vous perdrez les changements en cours si vous continuez.",
          confirmText: "Continuer la duplication",
        });
        if (!ok) return;
      }
      const { data } = await api.post(`/company-letters/${id}/duplicate`);
      toast.success(`Copie créée — ${data.reference}`);
      navigate(`/courriers/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur duplication");
    }
  };

  const toggleArchive = async () => {
    try {
      await api.put(`/company-letters/${id}`, { archived: !letter.archived });
      toast.success(letter.archived ? "Désarchivé" : "Archivé");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const downloadPdf = async (forcePreview = false) => {
    setGenerating(true);
    try {
      const blob = await pdf(<CompanyLetterPdf letter={letter} settings={settings} />).toBlob();
      const filename = `Courrier-${letter.reference}.pdf`;
      if (forcePreview) {
        showPdfPreview(blob, filename);
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch (e) {
      toast.error("Erreur génération PDF : " + (e?.message || e));
    } finally {
      setGenerating(false);
    }
  };

  // Auto-save soft : confirmation perte si on quitte avec changements non sauvegardés
  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const todayLocked = useMemo(() => letter?.archived === true, [letter]);

  if (!letter) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#575A57]">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="company-letter-detail">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <Link to="/courriers" className="text-[#575A57] hover:text-[#1A1C1A] mt-1" data-testid="company-letter-back">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-mono font-semibold">{letter.reference}</h1>
              {letter.archived && (
                <Badge className="bg-[#F0F0EB] text-[#737373] border-[#E5E5E0] text-[10px] uppercase">
                  Archivé
                </Badge>
              )}
              {dirty && (
                <Badge className="bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D9B8] text-[10px] uppercase">
                  Modifications non sauvegardées
                </Badge>
              )}
            </div>
            <p className="text-xs text-[#A3A6A3] mt-1">
              Créé le {fmtDate(letter.created_at)} · {letter.letter_type || "—"}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {!todayLocked && (
            <Button
              size="sm"
              onClick={saveAll}
              disabled={saving}
              className="bg-[#1F4E3D] hover:bg-[#173B2E]"
              data-testid="company-letter-save"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
              Enregistrer
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => downloadPdf(true)}
            disabled={generating}
            data-testid="company-letter-preview-pdf"
          >
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <FileText className="w-3.5 h-3.5 mr-1.5" />}
            Aperçu PDF
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => downloadPdf(false)}
            disabled={generating}
            data-testid="company-letter-download-pdf"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" /> Télécharger PDF
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={duplicate}
            data-testid="company-letter-duplicate-btn"
          >
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Dupliquer
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={toggleArchive}
            data-testid="company-letter-archive-btn"
          >
            {letter.archived
              ? <><ArchiveRestore className="w-3.5 h-3.5 mr-1.5" /> Désarchiver</>
              : <><Archive className="w-3.5 h-3.5 mr-1.5" /> Archiver</>}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={removeLetter}
            className="border-[#C05C47] text-[#C05C47] hover:bg-[#FCEEEA]"
            data-testid="company-letter-delete-btn"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Supprimer
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* DESTINATAIRE */}
        <Card className="border-[#E5E5E0]">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-semibold text-sm uppercase tracking-wider text-[#1F4E3D]">Destinataire</h2>
              {/* Phase 101df — Sélection depuis carnet d'adresses centralisé
                  Phase 101df-v2 — Chip contact lié + mini-carte au survol */}
              <AddressBookPicker
                testid="company-letter-address-book-picker"
                triggerLabel="Depuis le carnet"
                preferredRoles={["COURRIER", "REVENDEUR", "TRAITEUR"]}
                linkedContact={linkedContact}
                onUnlink={() => setLinkedContact(null)}
                onSelect={(c) => {
                  update({
                    recipient_company: c.organization || "",
                    recipient_name: c.name || "",
                    recipient_address: c.address || "",
                    recipient_postal_code: c.postal_code || "",
                    recipient_city: c.city || "",
                    recipient_country: c.country || "Suisse",
                    recipient_email: c.email || "",
                  });
                  setLinkedContact(c);
                  toast.success("Adresse importée depuis le carnet");
                }}
              />
            </div>
            <Field
              label="Société (optionnel)"
              value={letter.recipient_company}
              onChange={(v) => update({ recipient_company: v })}
              placeholder="Ex. Restaurant Le Lacustre Sàrl"
              testid="company-letter-recipient-company"
            />
            <Field
              label="Nom du contact"
              value={letter.recipient_name}
              onChange={(v) => update({ recipient_name: v })}
              placeholder="Ex. M. Dupont"
              testid="company-letter-recipient-name"
            />
            <Field
              label="Adresse (rue + n°)"
              value={letter.recipient_address}
              onChange={(v) => update({ recipient_address: v })}
              placeholder="Rue du Pont 12"
              testid="company-letter-recipient-address"
            />
            <div className="grid grid-cols-3 gap-3">
              <Field
                label="NPA"
                value={letter.recipient_postal_code}
                onChange={(v) => update({ recipient_postal_code: v })}
                placeholder="1003"
                testid="company-letter-recipient-postal"
              />
              <Field
                label="Ville"
                value={letter.recipient_city}
                onChange={(v) => update({ recipient_city: v })}
                placeholder="Lausanne"
                testid="company-letter-recipient-city"
                wrapperClass="col-span-2"
              />
            </div>
            <Field
              label="Pays"
              value={letter.recipient_country}
              onChange={(v) => update({ recipient_country: v })}
              placeholder="Suisse"
              testid="company-letter-recipient-country"
            />
            <Field
              label="Email (optionnel)"
              type="email"
              value={letter.recipient_email}
              onChange={(v) => update({ recipient_email: v })}
              placeholder="contact@exemple.ch"
              testid="company-letter-recipient-email"
            />
          </CardContent>
        </Card>

        {/* MÉTADONNÉES */}
        <Card className="border-[#E5E5E0]">
          <CardContent className="p-5 space-y-3">
            <h2 className="font-semibold text-sm uppercase tracking-wider text-[#1F4E3D]">Caractéristiques</h2>
            <Field
              label="Type de courrier (libre)"
              value={letter.letter_type}
              onChange={(v) => update({ letter_type: v })}
              placeholder="Information / Avertissement / Communication distributeur…"
              list="company-letter-type-suggestions"
              testid="company-letter-type"
            />
            <datalist id="company-letter-type-suggestions">
              {TYPE_SUGGESTIONS.map((t) => <option key={t} value={t} />)}
            </datalist>
            <Field
              label="Date du courrier"
              type="date"
              value={letter.letter_date}
              onChange={(v) => update({ letter_date: v })}
              testid="company-letter-date"
            />
            <Field
              label="Objet (sujet)"
              value={letter.subject}
              onChange={(v) => update({ subject: v })}
              placeholder="Ex. Mise à jour des conditions tarifaires 2026"
              testid="company-letter-subject"
            />
            <div className="text-xs text-[#A3A6A3] bg-[#FAFAF7] border border-[#E5E5E0] rounded p-3 leading-relaxed">
              <strong className="text-[#575A57]">Astuce :</strong> le type est libre — vous pouvez écrire ce que vous voulez
              (« Information », « Voeux », « Avertissement revendeur Sion »…). Il apparaîtra
              en haut du PDF en majuscules. Utilisez la commande <em>Dupliquer</em> pour réutiliser un courrier passé.
            </div>
          </CardContent>
        </Card>
      </div>

      {/* CORPS — Éditeur riche */}
      <Card className="border-[#E5E5E0]">
        <CardContent className="p-5 space-y-3">
          <h2 className="font-semibold text-sm uppercase tracking-wider text-[#1F4E3D]">Contenu du courrier</h2>
          <p className="text-xs text-[#575A57]">
            Utilisez la barre d&apos;outils pour mettre en forme (titres, gras, italique, listes, citations, liens).
            Le rendu PDF respectera scrupuleusement votre mise en page.
          </p>
          <RichTextEditor
            value={letter.body_json}
            onChange={(json, plain) => update({ body_json: json, body_plain: plain })}
            placeholder="Rédigez ici le corps du courrier (Madame, Monsieur, …)"
          />
        </CardContent>
      </Card>

      {/* Astuce signature + toggle « Inclure signature scannée » (Phase 101dd) */}
      <Card className="border-[#E5E5E0] bg-[#FAFAF7]">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="text-xs text-[#575A57] leading-relaxed flex-1 min-w-[220px]">
              <strong className="text-[#1A1C1A] block mb-1">Signature & en-tête société</strong>
              La signature, le logo et le pied de page sont automatiquement repris depuis
              <Link to="/parametres" className="text-[#1F4E3D] underline mx-1">Paramètres → Société</Link>
              (nom, adresse, signataire, rôle). Modifiez-les là une fois pour les appliquer à tous vos courriers.
            </div>
            <div className="flex items-center gap-3 shrink-0 bg-white border border-[#E5E5E0] rounded-md px-3 py-2">
              <div className="flex flex-col">
                <span className="text-xs font-medium text-[#1A1C1A]">Inclure signature scannée</span>
                <span className="text-[10px] text-[#A3A6A3]">
                  {settings?.company_signature_image
                    ? "Insère l'image de signature au-dessus du nom"
                    : "⚠ Aucune signature définie dans Paramètres"}
                </span>
              </div>
              <Switch
                checked={letter.include_signature_image === true}
                onCheckedChange={(v) => update({ include_signature_image: !!v })}
                disabled={!settings?.company_signature_image}
                data-testid="company-letter-include-signature-toggle"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
